import axios from "axios";
import { createPkcePair, generateRandomString } from "./pkce";

const SSO_STATE_KEY = "proview_sso_state";
const SSO_VERIFIER_KEY = "proview_sso_verifier";
// What the Login page asked for ({email, identityProvider, forced}), read back by the callback page.
const SSO_ATTEMPT_KEY = "proview_sso_attempt";
// The email of the last SSO sign-in, kept in localStorage so a new tab can offer "Continue as <email>". It is only a
// convenience: it is sent as a login hint and nothing is trusted from it. It survives logout and is removed only by "Use a different account".
const SSO_LAST_EMAIL_KEY = "proview_sso_last_email";
// Set when the user signed in through SSO, so logout also ends the Cognito session.
const SSO_LOGOUT_URL_KEY = "proview_sso_logout_url";

/**
 * Cognito settings come from the API at runtime (GET /api/sso/config), not from the frontend build.
 * One image is promoted through dev, qa, preprod and production, so per-environment values cannot be baked in.
 * Returns { enabled, cognitoDomain, clientId, defaultIdp }.
 */
export async function fetchSsoConfig() {
  const res = await axios.get("/api/sso/config");
  return res.data;
}

export function isSsoConfigured(config) {
  return Boolean(
    config && config.enabled && config.cognitoDomain && config.clientId
  );
}

function redirectUri() {
  return `${window.location.origin}/sso/callback`;
}

function logoutUri() {
  return `${window.location.origin}/login`;
}

/** Ask the API which IdP an email domain belongs to (used for email-first routing). */
export async function fetchIdpHint(email) {
  const res = await axios.get("/api/sso/idp-hint", { params: { email } });
  return res.data;
}

/**
 * Start the Cognito Hosted UI authorize flow (authorization code + PKCE) for one identity provider.
 * Passing identity_provider skips the Hosted UI chooser and goes straight to that IdP (e.g. M365).
 *
 * options.email: the User ID typed on the Login page. It is sent as login_hint (a non-binding hint to the IdP)
 *   and remembered so the callback can check that the account that signed in is the one that was typed.
 * options.forceLogin: adds prompt=login so Cognito and the IdP ask for credentials even when a session exists.
 *   Used only to retry after the existing session turned out to belong to a different account.
 */
export async function beginCognitoLogin(
  config,
  identityProvider,
  { email, forceLogin } = {}
) {
  if (!isSsoConfigured(config)) {
    throw new Error("SSO is not configured in this environment");
  }
  const { verifier, challenge } = await createPkcePair();
  const state = generateRandomString(32);

  sessionStorage.setItem(SSO_VERIFIER_KEY, verifier);
  sessionStorage.setItem(SSO_STATE_KEY, state);
  sessionStorage.setItem(
    SSO_ATTEMPT_KEY,
    JSON.stringify({
      email: email || "",
      identityProvider: identityProvider || "",
      forced: Boolean(forceLogin),
    })
  );

  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri(),
    state,
    code_challenge_method: "S256",
    code_challenge: challenge,
  });
  if (identityProvider) {
    params.set("identity_provider", identityProvider);
  }
  if (email) {
    params.set("login_hint", email);
  }
  if (forceLogin) {
    params.set("prompt", "login");
  }

  window.location.assign(
    `${config.cognitoDomain}/oauth2/authorize?${params.toString()}`
  );
}

/** The email of the last SSO sign-in in this browser, or "" when none is remembered. */
export function rememberedSsoEmail() {
  try {
    return localStorage.getItem(SSO_LAST_EMAIL_KEY) || "";
  } catch (e) {
    return "";
  }
}

export function rememberSsoEmail(email) {
  try {
    if (email) {
      localStorage.setItem(SSO_LAST_EMAIL_KEY, email);
    }
  } catch (e) {
    // Storage can be blocked; the page then simply shows the normal form.
  }
}

export function forgetSsoEmail() {
  try {
    localStorage.removeItem(SSO_LAST_EMAIL_KEY);
  } catch (e) {
    // Nothing to remove.
  }
}

/** Returns (and clears) the record stored by beginCognitoLogin, or null when there is none. */
export function takeSsoAttempt() {
  const raw = sessionStorage.getItem(SSO_ATTEMPT_KEY);
  sessionStorage.removeItem(SSO_ATTEMPT_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/**
 * Reads the email claim from a Cognito ID token WITHOUT verifying it. Only used to tell the user when the account
 * that signed in is not the one they typed. The API validates the token and decides who is signed in.
 */
export function emailFromIdToken(idToken) {
  try {
    const payload = idToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const claims = JSON.parse(atob(padded));
    return typeof claims.email === "string" ? claims.email : null;
  } catch (e) {
    return null;
  }
}

/** Exchange the authorization code for Cognito tokens (public client: PKCE verifier, no secret). */
export async function exchangeCodeForTokens(config, code, state) {
  const expectedState = sessionStorage.getItem(SSO_STATE_KEY);
  const verifier = sessionStorage.getItem(SSO_VERIFIER_KEY);
  sessionStorage.removeItem(SSO_STATE_KEY);
  sessionStorage.removeItem(SSO_VERIFIER_KEY);

  if (!expectedState || expectedState !== state) {
    throw new Error("Invalid SSO state. Please start the sign-in again.");
  }
  if (!verifier) {
    throw new Error("Missing PKCE verifier. Please start the sign-in again.");
  }
  if (!isSsoConfigured(config)) {
    throw new Error("SSO is not configured in this environment");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });

  const res = await axios.post(
    `${config.cognitoDomain}/oauth2/token`,
    body.toString(),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }
  );
  return res.data;
}

/** Remember where to send the browser on logout so the Cognito session is ended too. */
export function rememberCognitoLogout(config) {
  if (!isSsoConfigured(config)) {
    return;
  }
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: logoutUri(),
  });
  sessionStorage.setItem(
    SSO_LOGOUT_URL_KEY,
    `${config.cognitoDomain}/logout?${params.toString()}`
  );
}

/** Returns (and clears) the Cognito Hosted UI logout URL, or null if the user did not sign in via SSO. */
export function takeCognitoLogoutUrl() {
  const url = sessionStorage.getItem(SSO_LOGOUT_URL_KEY);
  sessionStorage.removeItem(SSO_LOGOUT_URL_KEY);
  return url;
}
